// Domain Health & Outage Correlation — Cyan Room design system

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pbAuthedFetch } from '../authHeaders';
import { useQuery } from '@tanstack/react-query';
import EditUrlsModal from '../components/EditUrlsModal';
import { SIMULATED_OUTAGE_SCENARIOS } from '../lib/outageFanout';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const CHART_COLORS = [
  'var(--gold)',
  'var(--ok)',
  'var(--risk)',
  'var(--ink-45)',
  'var(--navy)',
  '#6C8EBF',
  '#D4A574',
  '#8B6FC0',
];

function gaugeColor(pct: number) {
  if (pct < 99.0) return 'var(--risk)';
  if (pct < 99.9) return 'var(--gold)';
  return 'var(--ok)';
}

function sslColor(days: number) {
  if (days < 30) return 'var(--risk)';
  if (days < 90) return 'var(--gold)';
  return 'var(--ok)';
}

/* ---------- SVG Arc Gauge (270°) ---------- */

function UptimeGauge({ domain, uptimePercent }: { domain: string; uptimePercent: number }) {
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const progress = (uptimePercent / 100) * circumference;
  const color = gaugeColor(uptimePercent);

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-1)', color: 'var(--ink)' }}>
        {domain}
      </div>
      <svg width="180" height="120" viewBox="0 0 180 120">
        <circle
          cx="90" cy="90" r={radius}
          fill="none" stroke="var(--hair)" strokeWidth="12"
          strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
          transform="rotate(135 90 90)"
        />
        <circle
          cx="90" cy="90" r={radius}
          fill="none" stroke={color} strokeWidth="12"
          strokeDasharray={`${progress * 0.75} ${circumference - progress * 0.75}`}
          transform="rotate(135 90 90)"
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
        <text x="90" y="85" textAnchor="middle" fill={color} fontSize="28" fontWeight="bold">
          {uptimePercent.toFixed(2)}%
        </text>
        <text x="90" y="105" textAnchor="middle" fill="var(--ink-45)" fontSize="11">
          1h uptime
        </text>
      </svg>
    </div>
  );
}

/* ---------- Status Tile Grid ---------- */

function StatusGrid({
  domains,
  field,
}: {
  domains: { domain: string }[];
  field: 'httpsUp' | 'httpUp';
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
      {domains.map((d) => {
        const up = (d as any)[field];
        return (
          <span key={d.domain} className={`pill ${up ? 'pill--ok' : 'pill--risk'}`}>
            <i></i>
            {d.domain} — {up ? 'ONLINE' : 'OFFLINE'}
          </span>
        );
      })}
    </div>
  );
}

/* ── domain health panel ─────────────────────────────────── */

function DomainHealthPanel() {
  const [editOpen, setEditOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['domain-health'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/domain-health');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="page-loading" role="status" aria-live="polite">Loading domain health…</div>;
  }

  if (isError) {
    return (
      <div className="state state--risk">
        <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h3 className="state__title">Failed to load domain health data</h3>
        <p className="state__body">
          Could not connect to the domain health API. Please check your connection and try again.
        </p>
        <button className="btn btn--primary" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const { domains = [], responseTimePoints = [], mock } = data ?? {};

  // Pivot responseTimePoints into wide format for Recharts
  const domainNames = [...new Set(domains.map((d: any) => d.domain))] as string[];
  const timeMap = new Map<string, Record<string, number>>();
  for (const pt of responseTimePoints) {
    if (!timeMap.has(pt.timestamp)) timeMap.set(pt.timestamp, { timestamp: pt.timestamp });
    timeMap.get(pt.timestamp)![pt.domain] = pt.value;
  }
  const chartData = [...timeMap.values()].sort((a, b) =>
    String(a.timestamp ?? '').localeCompare(String(b.timestamp ?? '')),
  );

  const hasAlerts = domains.some((d: any) => d.alerts?.length > 0);
  const onlineCount = domains.filter((d: any) => d.httpsUp).length;

  return (
    <>
      {editOpen && <EditUrlsModal onClose={() => setEditOpen(false)} />}

      {/* Facts + actions bar */}
      <div className="notice notice--info" style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap', width: '100%' }}>
          <p className="notice__body" style={{ margin: 0 }}>
            Real-time probe status via Blackbox Exporter · Auto-refresh every <b>30s</b> · Domains: <b>{domains.length}</b> · Online: <b>{onlineCount}/{domains.length}</b>
          </p>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn--sm" onClick={() => setEditOpen(true)}>✎ Edit</button>
          </div>
        </div>
      </div>

      {/* Mock banner */}
      {mock && (
        <div className="notice notice--warn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <p className="notice__title">Demo mode</p>
            <p className="notice__body">Prometheus not connected. Showing sample data.</p>
          </div>
        </div>
      )}

      {/* Alert banner */}
      {hasAlerts && (
        <div className="notice notice--warn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div>
            <p className="notice__title">Active Alerts</p>
            <ul className="notice__body" style={{ margin: 'var(--sp-2) 0 0', paddingLeft: 'var(--sp-5)' }}>
              {domains.flatMap((d: any) =>
                (d.alerts || []).map((a: any) => (
                  <li key={`${d.domain}-${a.metric}`} style={{ color: a.level === 'critical' ? 'var(--risk)' : 'var(--gold)', fontSize: 'var(--text-sm)' }}>
                    <strong>{d.domain}</strong>: {a.message}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Status row — HTTPS / HTTP */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Probes</p>
              <h2 className="box__title">HTTPS Status</h2>
            </div>
          </div>
          <div className="box__body">
            <StatusGrid domains={domains} field="httpsUp" />
          </div>
        </div>
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Probes</p>
              <h2 className="box__title">HTTP Status</h2>
            </div>
          </div>
          <div className="box__body">
            <StatusGrid domains={domains} field="httpUp" />
          </div>
        </div>
      </div>

      {/* Response time chart */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Performance</p>
            <h2 className="box__title">Response Time</h2>
          </div>
        </div>
        <div className="box__body">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                <XAxis
                  dataKey="timestamp"
                  tick={{ fontSize: 11, fill: 'var(--ink-45)' }}
                  tickFormatter={(v: string) =>
                    new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  }
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--ink-45)' }}
                  label={{
                    value: 'ms',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: 'var(--ink-45)' },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface)',
                    border: '1px solid var(--hair)',
                    borderRadius: 'var(--r-ctl)',
                    fontSize: 'var(--text-sm)',
                  }}
                  labelFormatter={(v) => new Date(String(v)).toLocaleString()}
                />
                <Legend wrapperStyle={{ fontSize: 'var(--text-sm)' }} />
                {domainNames.map((name, i) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <h3 className="state__title">No response time data</h3>
              <p className="state__body">Prometheus is not connected — response time history is unavailable.</p>
            </div>
          )}
        </div>
      </div>

      {/* SSL certificate panel */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Security</p>
            <h2 className="box__title">SSL Certificate Expiry</h2>
          </div>
        </div>
        <div className="box__body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
            {domains.map((d: any) => {
              const days = d.sslExpiryDays ?? 0;
              const daysRounded = Math.round(days);
              const weeks = (days / 7).toFixed(1);
              const color = sslColor(days);
              return (
                <div
                  key={d.domain}
                  className="box"
                  style={{
                    borderColor: color,
                    padding: 'var(--sp-3) var(--sp-4)',
                    minWidth: 140,
                  }}
                >
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)' }}>{d.domain}</div>
                  <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color, fontFamily: 'var(--mono)' }}>
                    {daysRounded}d
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)' }}>{weeks} weeks</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Uptime gauges */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Availability</p>
            <h2 className="box__title">Uptime Gauges (1 h)</h2>
          </div>
        </div>
        <div className="box__body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-5)', justifyContent: 'flex-start' }}>
            {domains.map((d: any) => (
              <UptimeGauge key={d.domain} domain={d.domain} uptimePercent={d.uptimePercent ?? 0} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* ── outages panel ───────────────────────────────────────── */

function OutagesPanel() {
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['outages-summary'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/outages/summary');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: noiseData, isLoading: noiseLoading } = useQuery({
    queryKey: ['outages-noise'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/outages/noise');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: unownedData, isLoading: unownedLoading } = useQuery({
    queryKey: ['outages-unowned'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/outages/unowned');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: overlayData, isLoading: overlayLoading } = useQuery({
    queryKey: ['outages-change-overlay'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/outages/change-overlay');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const flapping = noiseData?.flapping || [];
  const topOffenders = noiseData?.top_offenders || [];
  const unowned = unownedData?.alerts || [];
  const noiseRatio = summary?.noise_ratio;
  const correlatedChanges = overlayData?.correlated_changes || [];

  const isLoading = summaryLoading || noiseLoading || unownedLoading || overlayLoading;

  const flappingColumns: Column<any>[] = [
    { key: 'name', label: 'Alert', accessor: (r: any) => r.name || r.title, render: (r: any) => <span className="td--name">{r.name || r.title}</span> },
    { key: 'source', label: 'Source', render: (r: any) => <span className="td--id">{r.source}</span> },
    { key: 'fire_count', label: 'Fires', render: (r: any) => <span className="td--num" style={{ color: 'var(--risk)', fontWeight: 600 }}>{r.fire_count ?? '—'}</span> },
    { key: 'resolve_count', label: 'Resolves', render: (r: any) => <span className="td--num">{r.resolve_count ?? '—'}</span> },
    { key: 'window', label: 'Window', sortable: false, render: (r: any) => (<span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.window_start ? new Date(r.window_start).toLocaleDateString() : '—'}{' – '}{r.window_end ? new Date(r.window_end).toLocaleDateString() : '—'}</span>) },
    { key: 'severity', label: 'Severity', render: (r: any) => <span className={`pill pill--${r.severity === 'critical' ? 'risk' : r.severity === 'high' ? 'warn' : 'ok'}`}><i></i>{r.severity || 'unknown'}</span> },
  ];
  const flappingCtrl = useTableControls(flapping, flappingColumns, { storageKey: 'outages-flapping' });

  const topOffendersColumns: Column<any>[] = [
    { key: 'name', label: 'Alert', accessor: (r: any) => r.name || r.title, render: (r: any) => <span className="td--name">{r.name || r.title}</span> },
    { key: 'source', label: 'Source', render: (r: any) => <span className="td--id">{r.source}</span> },
    { key: 'volume', label: 'Volume', render: (r: any) => <span className="td--num" style={{ fontWeight: 600 }}>{r.volume}</span> },
    { key: 'pct', label: '% of Total', sortable: false, render: (r: any) => (<div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}><div style={{ width: `${Math.min((r.volume / (topOffenders[0]?.volume || 1)) * 100, 100)}%`, height: 6, background: 'var(--risk)', borderRadius: 'var(--r-xs)', minWidth: 4 }} /><span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{summary?.total_alerts ? `${((r.volume / summary.total_alerts) * 100).toFixed(1)}%` : '—'}</span></div>) },
  ];
  const topOffendersCtrl = useTableControls(topOffenders, topOffendersColumns, { defaultSortKey: 'volume', defaultSortDir: 'desc', storageKey: 'outages-top-offenders' });

  const unownedColumns: Column<any>[] = [
    { key: 'name', label: 'Alert', accessor: (r: any) => r.name || r.title, render: (r: any) => <span className="td--name">{r.name || r.title}</span> },
    { key: 'source', label: 'Source', render: (r: any) => <span className="td--id">{r.source}</span> },
    { key: 'target_ci', label: 'Target CI', render: (r: any) => <span className="td--id">{r.target_ci || '—'}</span> },
    { key: 'first_seen_at', label: 'First Seen', dateField: true, render: (r: any) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.first_seen_at ? new Date(r.first_seen_at).toLocaleDateString() : '—'}</span> },
    { key: 'fire_count', label: 'Fire Count', render: (r: any) => <span className="td--num">{r.fire_count ?? '—'}</span> },
  ];
  const unownedCtrl = useTableControls(unowned, unownedColumns, { storageKey: 'outages-unowned' });

  const overlayColumns: Column<any>[] = [
    { key: 'title', label: 'Change', accessor: (r: any) => `${r.title || r.id} ${r.implementer || ''}`, render: (r: any) => (<><div className="td--name">{r.title || r.id}</div><div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.implementer || '—'}</div></>) },
    { key: 'alerts_triggered', label: 'Alerts Triggered', render: (r: any) => <span className="td--num" style={{ fontWeight: 600 }}>{r.alerts_triggered}</span> },
    { key: 'time_to_first_alert', label: 'Time to First Alert', render: (r: any) => <span className="td--num">{r.time_to_first_alert != null ? `${r.time_to_first_alert}m` : '—'}</span> },
    { key: 'confidence', label: 'Confidence', render: (r: any) => <span className={`pill pill--${r.confidence === 'high' ? 'warn' : r.confidence === 'medium' ? 'ok' : 'risk'}`}><i></i>{r.confidence || 'unknown'}</span> },
  ];
  const overlayCtrl = useTableControls(correlatedChanges, overlayColumns, { storageKey: 'outages-change-overlay' });

  const navigate = useNavigate();
  const [simOpen, setSimOpen] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [simToast, setSimToast] = useState<{ msg: string; isError: boolean } | null>(null);

  const triggerSimulation = useCallback(async (scenarioKey: string) => {
    setSimOpen(false);
    setSimLoading(true);
    try {
      const res = await pbAuthedFetch('/api/outages/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario: scenarioKey }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed: ${res.status}`);
      }
      // On success, navigate to the shareable Outage Pipeline page for this
      // incident instead of opening the fan-out overlay in place.
      const body = await res.json().catch(() => ({}));
      const id = body.incident?.id;
      if (!id) throw new Error('Simulate response did not include an incident id');
      navigate(`/outage-pipeline/${id}`);
    } catch (err) {
      setSimToast({ msg: (err as Error).message, isError: true });
      setTimeout(() => setSimToast(null), 5000);
    } finally {
      setSimLoading(false);
    }
  }, [navigate]);

  if (isLoading) {
    return <div className="page-loading" role="status" aria-live="polite">Loading outage data…</div>;
  }

  if (summaryError) {
    return (
      <div className="state state--risk">
        <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h3 className="state__title">Failed to load outage data</h3>
        <p className="state__body">
          Could not connect to the outages API. Please check your connection and try again.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Facts + actions bar */}
      <div className="notice notice--info" style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap', width: '100%', position: 'relative' }}>
          <p className="notice__body" style={{ margin: 0 }}>
            Alert noise reduction — separate signal from churn
            {summary?.total_alerts != null && <> · Alerts: <b>{summary.total_alerts}</b></>}
          </p>
          <div style={{ marginLeft: 'auto', position: 'relative' }}>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setSimOpen((o) => !o)}
              disabled={simLoading}
            >
              {simLoading ? 'Simulating…' : 'Simulate Outage'}
            </button>
            {simOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 6,
                  width: 320,
                  background: 'var(--surface)',
                  border: '1px solid var(--hair)',
                  borderRadius: 'var(--r-box)',
                  boxShadow: 'var(--sh-lift)',
                  padding: 10,
                  zIndex: 20,
                }}
              >
                {SIMULATED_OUTAGE_SCENARIOS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className="ing-demo-item"
                    onClick={() => triggerSimulation(s.key)}
                  >
                    <span className="ing-demo-name">{s.label}</span>
                    <span className="ing-demo-desc">{s.description}</span>
                    <span className="ing-demo-arrow" aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {simToast && (
        <div className="notice notice--risk" style={{ marginBottom: 'var(--sp-4)' }}>
          {simToast.msg}
        </div>
      )}

      {/* Completeness statement */}
      <div className="notice notice--info">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <p className="notice__title">Analysis Window</p>
          <p className="notice__body">
            {summary?.total_alerts != null ? (
              <>
                <strong>{summary.total_alerts}</strong> alerts in the analysis window.
                {' '}<strong>{summary.linked_to_incidents ?? 0}</strong> linked to incidents.
              </>
            ) : (
              'No outage data ingested yet.'
            )}
          </p>
        </div>
      </div>

      {/* Metric Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Outage summary metrics">
        <div className="ind ind--watch">
          <div className="ind__head">
            <p className="ind__abbr">LINKAGE</p>
            <p className="ind__full">Alert-to-Incident Linkage</p>
          </div>
          <div className="ind__value">
            <p className="ind__v" style={{ color: 'var(--ok)' }}>
              {summary?.linkage_rate != null
                ? `${(summary.linkage_rate * 100).toFixed(0)}%`
                : '—'}
            </p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--ok)' }}></i>
            <b>{summary?.linked_to_incidents ?? 0}</b> of {summary?.total_alerts ?? 0} alerts linked
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">MTTA</p>
            <p className="ind__full">True Mean Time to Acknowledge</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">
              {summary?.true_mtta != null ? `${summary.true_mtta}m` : '—'}
            </p>
          </div>
          <p className="ind__reading">
            <i></i> Real incidents only
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">MTTR</p>
            <p className="ind__full">True Mean Time to Resolve</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">
              {summary?.true_mttr != null ? `${summary.true_mttr}m` : '—'}
            </p>
          </div>
          <p className="ind__reading">
            <i></i> Real incidents only
          </p>
        </div>

        <div className={`ind ${noiseRatio != null && noiseRatio > 0.5 ? 'ind--breach' : ''}`}>
          <div className="ind__head">
            <p className="ind__abbr">NOISE</p>
            <p className="ind__full">Noise Ratio</p>
          </div>
          <div className="ind__value">
            <p className="ind__v" style={noiseRatio != null && noiseRatio > 0.5 ? { color: 'var(--risk)' } : undefined}>
              {noiseRatio != null ? `${(noiseRatio * 100).toFixed(0)}%` : '—'}
            </p>
          </div>
          <p className="ind__reading">
            <i style={noiseRatio != null && noiseRatio > 0.5 ? { background: 'var(--risk)' } : undefined}></i>
            {noiseRatio != null && noiseRatio > 0.5
              ? <><b style={{ color: 'var(--risk)' }}>High</b> — alerts not corresponding to real incidents</>
              : <>Alerts that did not correspond to real incidents</>
            }
          </p>
        </div>
      </div>

      {/* Flapping Alerts */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Noise Analysis</p>
            <h2 className="box__title">Flapping Alerts</h2>
          </div>
          <div className="box__head-actions">
            <span className="fact">{flapping.length} alerts</span>
          </div>
        </div>
        <div className="box__body box__body--flush">
          <DataTable
            columns={flappingColumns}
            visibleColumns={flappingCtrl.visibleColumns}
            data={flappingCtrl.paged}
            search={flappingCtrl.search}
            onSearchChange={flappingCtrl.setSearch}
            sortKey={flappingCtrl.sortKey}
            sortDir={flappingCtrl.sortDir}
            onSort={flappingCtrl.setSort}
            page={flappingCtrl.page}
            totalPages={flappingCtrl.totalPages}
            totalFiltered={flappingCtrl.totalFiltered}
            pageSize={flappingCtrl.pageSize}
            onPageChange={flappingCtrl.setPage}
            onPageSizeChange={flappingCtrl.setPageSize}
            activeFilters={flappingCtrl.activeFilters}
            onFilter={flappingCtrl.setFilter}
            onClearFilters={flappingCtrl.clearFilters}
            timeRange={flappingCtrl.timeRange}
            onTimeRangeChange={flappingCtrl.setTimeRange}
            hiddenColumns={flappingCtrl.hiddenColumns}
            onToggleColumn={flappingCtrl.toggleColumn}
            selectedIds={flappingCtrl.selectedIds}
            onToggleSelect={flappingCtrl.toggleSelect}
            onSelectAll={flappingCtrl.selectAll}
            onClearSelection={flappingCtrl.clearSelection}
            savedViews={flappingCtrl.savedViews}
            onSaveView={flappingCtrl.saveView}
            onLoadView={flappingCtrl.loadView}
            onDeleteView={flappingCtrl.deleteView}
            onExportCSV={flappingCtrl.exportCSV}
            onExportExcel={flappingCtrl.exportExcel}
            searchPlaceholder="Search flapping alerts..."
            keyAccessor={(r: any) => r.id || r.name || r.title}
            emptyMessage="No flapping alerts detected in the current window."
          />
        </div>
      </div>

      {/* Top-10 Offenders */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Noise Analysis</p>
            <h2 className="box__title">Top-10 Alert Offenders</h2>
          </div>
          <div className="box__head-actions">
            <span className="fact">By volume</span>
          </div>
        </div>
        <div className="box__body box__body--flush">
          <DataTable
            columns={topOffendersColumns}
            visibleColumns={topOffendersCtrl.visibleColumns}
            data={topOffendersCtrl.paged}
            search={topOffendersCtrl.search}
            onSearchChange={topOffendersCtrl.setSearch}
            sortKey={topOffendersCtrl.sortKey}
            sortDir={topOffendersCtrl.sortDir}
            onSort={topOffendersCtrl.setSort}
            page={topOffendersCtrl.page}
            totalPages={topOffendersCtrl.totalPages}
            totalFiltered={topOffendersCtrl.totalFiltered}
            pageSize={topOffendersCtrl.pageSize}
            onPageChange={topOffendersCtrl.setPage}
            onPageSizeChange={topOffendersCtrl.setPageSize}
            activeFilters={topOffendersCtrl.activeFilters}
            onFilter={topOffendersCtrl.setFilter}
            onClearFilters={topOffendersCtrl.clearFilters}
            timeRange={topOffendersCtrl.timeRange}
            onTimeRangeChange={topOffendersCtrl.setTimeRange}
            hiddenColumns={topOffendersCtrl.hiddenColumns}
            onToggleColumn={topOffendersCtrl.toggleColumn}
            selectedIds={topOffendersCtrl.selectedIds}
            onToggleSelect={topOffendersCtrl.toggleSelect}
            onSelectAll={topOffendersCtrl.selectAll}
            onClearSelection={topOffendersCtrl.clearSelection}
            savedViews={topOffendersCtrl.savedViews}
            onSaveView={topOffendersCtrl.saveView}
            onLoadView={topOffendersCtrl.loadView}
            onDeleteView={topOffendersCtrl.deleteView}
            onExportCSV={topOffendersCtrl.exportCSV}
            onExportExcel={topOffendersCtrl.exportExcel}
            searchPlaceholder="Search offenders..."
            keyAccessor={(r: any) => r.id || r.name || r.title}
            emptyMessage="No alert volume data available."
          />
        </div>
      </div>

      {/* Unowned Alerts */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Ownership</p>
            <h2 className="box__title">Unowned Alerts</h2>
          </div>
          <div className="box__head-actions">
            <a href="/reconciliation" className="btn btn--sm">
              View in Reconciliation →
            </a>
          </div>
        </div>
        <div className="box__body box__body--flush">
          <DataTable
            columns={unownedColumns}
            visibleColumns={unownedCtrl.visibleColumns}
            data={unownedCtrl.paged}
            search={unownedCtrl.search}
            onSearchChange={unownedCtrl.setSearch}
            sortKey={unownedCtrl.sortKey}
            sortDir={unownedCtrl.sortDir}
            onSort={unownedCtrl.setSort}
            page={unownedCtrl.page}
            totalPages={unownedCtrl.totalPages}
            totalFiltered={unownedCtrl.totalFiltered}
            pageSize={unownedCtrl.pageSize}
            onPageChange={unownedCtrl.setPage}
            onPageSizeChange={unownedCtrl.setPageSize}
            activeFilters={unownedCtrl.activeFilters}
            onFilter={unownedCtrl.setFilter}
            onClearFilters={unownedCtrl.clearFilters}
            timeRange={unownedCtrl.timeRange}
            onTimeRangeChange={unownedCtrl.setTimeRange}
            hiddenColumns={unownedCtrl.hiddenColumns}
            onToggleColumn={unownedCtrl.toggleColumn}
            selectedIds={unownedCtrl.selectedIds}
            onToggleSelect={unownedCtrl.toggleSelect}
            onSelectAll={unownedCtrl.selectAll}
            onClearSelection={unownedCtrl.clearSelection}
            savedViews={unownedCtrl.savedViews}
            onSaveView={unownedCtrl.saveView}
            onLoadView={unownedCtrl.loadView}
            onDeleteView={unownedCtrl.deleteView}
            onExportCSV={unownedCtrl.exportCSV}
            onExportExcel={unownedCtrl.exportExcel}
            searchPlaceholder="Search unowned alerts..."
            keyAccessor={(r: any) => r.id || r.name || r.title}
            emptyMessage="No unowned alerts. All alerts map to a known owner or CI."
          />
        </div>
      </div>

      {/* Change Overlay */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Correlation</p>
            <h2 className="box__title">Change-Overlay Correlation</h2>
          </div>
        </div>
        <div className="box__body">
          {overlayData ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)', marginBottom: 'var(--sp-5)' }} role="group" aria-label="Change overlay metrics">
                <div className="ind ind--watch">
                  <div className="ind__head">
                    <p className="ind__abbr">CORR</p>
                    <p className="ind__full">Correlation Rate</p>
                  </div>
                  <div className="ind__value">
                    <p className="ind__v" style={{ color: 'var(--gold)' }}>
                      {overlayData?.correlation_rate != null
                        ? `${(overlayData.correlation_rate * 100).toFixed(0)}%`
                        : '—'}
                    </p>
                  </div>
                  <p className="ind__reading">
                    <i style={{ background: 'var(--gold)' }}></i>
                    Alerts that fired within the change window
                  </p>
                </div>

                <div className="ind">
                  <div className="ind__head">
                    <p className="ind__abbr">CI 95%</p>
                    <p className="ind__full">Confidence Interval</p>
                  </div>
                  <div className="ind__value">
                    <p className="ind__v">
                      {overlayData?.ci_lower != null && overlayData?.ci_upper != null
                        ? `${(overlayData.ci_lower * 100).toFixed(0)}–${(overlayData.ci_upper * 100).toFixed(0)}%`
                        : '—'}
                    </p>
                  </div>
                  <p className="ind__reading">
                    <i></i> 95% CI for correlation rate
                  </p>
                </div>

                <div className="ind">
                  <div className="ind__head">
                    <p className="ind__abbr">CHNG</p>
                    <p className="ind__full">Changes in Window</p>
                  </div>
                  <div className="ind__value">
                    <p className="ind__v">
                      {overlayData?.total_changes ?? '—'}
                    </p>
                  </div>
                  <p className="ind__reading">
                    <i></i> Change records evaluated
                  </p>
                </div>
              </div>

              <div className="box__body--flush">
                <DataTable
                  columns={overlayColumns}
                  visibleColumns={overlayCtrl.visibleColumns}
                  data={overlayCtrl.paged}
                  search={overlayCtrl.search}
                  onSearchChange={overlayCtrl.setSearch}
                  sortKey={overlayCtrl.sortKey}
                  sortDir={overlayCtrl.sortDir}
                  onSort={overlayCtrl.setSort}
                  page={overlayCtrl.page}
                  totalPages={overlayCtrl.totalPages}
                  totalFiltered={overlayCtrl.totalFiltered}
                  pageSize={overlayCtrl.pageSize}
                  onPageChange={overlayCtrl.setPage}
                  onPageSizeChange={overlayCtrl.setPageSize}
                  activeFilters={overlayCtrl.activeFilters}
                  onFilter={overlayCtrl.setFilter}
                  onClearFilters={overlayCtrl.clearFilters}
                  timeRange={overlayCtrl.timeRange}
                  onTimeRangeChange={overlayCtrl.setTimeRange}
                  hiddenColumns={overlayCtrl.hiddenColumns}
                  onToggleColumn={overlayCtrl.toggleColumn}
                  selectedIds={overlayCtrl.selectedIds}
                  onToggleSelect={overlayCtrl.toggleSelect}
                  onSelectAll={overlayCtrl.selectAll}
                  onClearSelection={overlayCtrl.clearSelection}
                  savedViews={overlayCtrl.savedViews}
                  onSaveView={overlayCtrl.saveView}
                  onLoadView={overlayCtrl.loadView}
                  onDeleteView={overlayCtrl.deleteView}
                  onExportCSV={overlayCtrl.exportCSV}
                  onExportExcel={overlayCtrl.exportExcel}
                  searchPlaceholder="Search changes..."
                  keyAccessor={(r: any) => r.id || r.title}
                  emptyMessage="No correlated changes found."
                />
              </div>
            </>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <h3 className="state__title">No change-overlay data</h3>
              <p className="state__body">
                Link change records to enable correlation analysis.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ── page ────────────────────────────────────────────────── */

type HealthTab = 'health' | 'outages';

function initialTab(): HealthTab {
  try {
    return new URLSearchParams(window.location.search).get('tab') === 'outages' ? 'outages' : 'health';
  } catch {
    return 'health';
  }
}

export function DomainHealthPage() {
  const [tab, setTab] = useState<HealthTab>(initialTab);

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">{tab === 'health' ? 'Infrastructure' : 'Outages'}</p>
            <h1 className="pagehead__title">{tab === 'health' ? 'Domain Health' : 'Outage Correlation'}</h1>
            <div className="pagehead__facts">
              <span className="fact">
                {tab === 'health'
                  ? 'Real-time probe status via Blackbox Exporter'
                  : 'Alert noise reduction — separate signal from churn'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        <div className="tabs" role="tablist" aria-label="Domain health view">
          <button
            className="tab"
            role="tab"
            aria-selected={tab === 'health'}
            onClick={() => setTab('health')}
          >
            Domain Health
          </button>
          <button
            className="tab"
            role="tab"
            aria-selected={tab === 'outages'}
            onClick={() => setTab('outages')}
          >
            Outages
          </button>
        </div>

        {tab === 'health' ? <DomainHealthPanel /> : <OutagesPanel />}
      </div>
    </>
  );
}
